/**
 * Trocha66 · piezas interactivas de las colecciones (Goggles, SEITA).
 * Todo es HTML que ya funciona sin JavaScript; este archivo solo lo mejora.
 *
 * Elementos:
 *  - <t66-tilt>      paralaje suave del producto al mover el mouse sobre [data-t66-tilt-area].
 *  - <t66-stage>     selector (pestañas + paneles). Copia los data-var-* de la pestaña activa como
 *                    variables CSS (--t66-*) y como texto en los [data-bind] (con conteo animado
 *                    si el elemento lleva data-bind-tween).
 *  - <t66-compare>   comparador con deslizador nativo (input range) y un barrido de bienvenida.
 *  - <t66-hotspots>  puntos sobre una foto enlazados con una lista de notas.
 *  - <t66-countup>   número que cuenta al entrar en pantalla.
 *  - <t66-rain>      lluvia en canvas sobre el hero, con viento según el mouse y botón para apagarla.
 *  - <t66-strips>    franjas de foto que se expanden (una activa a la vez).
 *  - <t66-reveal>    marca data-visible al entrar en pantalla (para animar barras con CSS).
 *  - <t66-scrolly>   lookbook con scroll: sección alta con un escenario fijo (sticky) y paso activo según el avance.
 *
 * @example
 * <t66-stage>
 *   <div data-stage-tabs><a href="/products/x" data-stage-tab data-var-accent="#2f55c6">Isola</a></div>
 *   <article data-stage-panel>…</article>
 * </t66-stage>
 */

const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
const finePointer = () => matchMedia('(hover: hover) and (pointer: fine)').matches;

/**
 * @param {number} from
 * @param {number} to
 * @param {number} duration milisegundos
 * @param {(value: number) => void} onFrame
 * @returns {() => void} cancela la animación
 */
function tween(from, to, duration, onFrame) {
  if (reducedMotion() || duration <= 0 || from === to) {
    onFrame(to);
    return () => {};
  }
  let frame = 0;
  const start = performance.now();
  const step = (now) => {
    const t = Math.min(1, Math.max(0, (now - start) / duration));
    const eased = 1 - Math.pow(1 - t, 3);
    onFrame(from + (to - from) * eased);
    if (t < 1) frame = requestAnimationFrame(step);
  };
  frame = requestAnimationFrame(step);
  return () => cancelAnimationFrame(frame);
}

/** @param {string} value */
const toPascal = (value) => value.charAt(0).toUpperCase() + value.slice(1);

class T66Tilt extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;
  #frame = 0;

  connectedCallback() {
    if (reducedMotion() || !finePointer()) return;

    const area = this.closest('[data-t66-tilt-area]');
    if (!(area instanceof HTMLElement)) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    area.addEventListener(
      'pointermove',
      (event) => {
        cancelAnimationFrame(this.#frame);
        this.#frame = requestAnimationFrame(() => {
          const rect = area.getBoundingClientRect();
          if (!rect.width || !rect.height) return;
          const x = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
          const y = ((event.clientY - rect.top) / rect.height - 0.5) * 2;
          area.style.setProperty('--t66-tx', x.toFixed(3));
          area.style.setProperty('--t66-ty', y.toFixed(3));
        });
      },
      { signal }
    );
    area.addEventListener(
      'pointerleave',
      () => {
        cancelAnimationFrame(this.#frame);
        area.style.setProperty('--t66-tx', '0');
        area.style.setProperty('--t66-ty', '0');
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#abort?.abort();
    cancelAnimationFrame(this.#frame);
  }
}

class T66Stage extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;
  /** @type {HTMLElement[]} */
  #tabs = [];
  /** @type {HTMLElement[]} */
  #panels = [];
  /** @type {Map<HTMLElement, () => void>} */
  #tweens = new Map();
  #index = -1;

  connectedCallback() {
    this.#tabs = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-stage-tab]')]);
    this.#panels = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-stage-panel]')]);
    if (this.#tabs.length < 2) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.querySelector('[data-stage-tabs]')?.setAttribute('role', 'tablist');
    this.#panels.forEach((panel) => panel.setAttribute('role', 'tabpanel'));
    this.#tabs.forEach((tab, index) => {
      tab.setAttribute('role', 'tab');
      tab.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          this.#select(index);
        },
        { signal }
      );
      if (finePointer()) {
        tab.addEventListener('pointerenter', () => this.#select(index), { signal });
      }
      tab.addEventListener('keydown', (event) => this.#onKeydown(event, index), { signal });
    });

    this.dataset.enhanced = '';
    const initial = this.#tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    this.#select(initial === -1 ? 0 : initial, { animate: false });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#tweens.forEach((cancel) => cancel());
    this.#tweens.clear();
  }

  /**
   * @param {KeyboardEvent} event
   * @param {number} index
   */
  #onKeydown(event, index) {
    const last = this.#tabs.length - 1;
    /** @type {Record<string, number>} */
    const targets = {
      ArrowDown: index === last ? 0 : index + 1,
      ArrowRight: index === last ? 0 : index + 1,
      ArrowUp: index === 0 ? last : index - 1,
      ArrowLeft: index === 0 ? last : index - 1,
      Home: 0,
      End: last,
    };
    if (!(event.key in targets)) return;
    event.preventDefault();
    this.#select(targets[event.key]);
    this.#tabs[targets[event.key]].focus();
  }

  /**
   * @param {number} index
   * @param {{ animate?: boolean }} [options]
   */
  #select(index, { animate = true } = {}) {
    if (index === this.#index) return;
    this.#index = index;

    this.#tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      if (selected) tab.dataset.active = '';
      else delete tab.dataset.active;
    });
    this.#panels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });
    this.dataset.index = String(index);
    this.#applyVars(this.#tabs[index], animate);
  }

  /**
   * @param {HTMLElement} tab
   * @param {boolean} animate
   */
  #applyVars(tab, animate) {
    for (const [key, value] of Object.entries(tab.dataset)) {
      if (!key.startsWith('var') || key.length < 4 || value === undefined) continue;
      const name = key.slice(3).replace(/^./, (char) => char.toLowerCase());
      this.style.setProperty(`--t66-${name}`, value);
    }

    for (const target of this.querySelectorAll('[data-bind]')) {
      if (!(target instanceof HTMLElement)) continue;
      const raw = tab.dataset[`var${toPascal(target.dataset.bind ?? '')}`];
      if (raw === undefined) continue;

      this.#tweens.get(target)?.();
      const to = Number(raw);
      if (!animate || !('bindTween' in target.dataset) || Number.isNaN(to)) {
        target.textContent = raw;
        continue;
      }
      const from = Number(target.textContent) || 0;
      this.#tweens.set(
        target,
        tween(from, to, 550, (value) => {
          target.textContent = String(Math.round(value));
        })
      );
    }
  }
}

class T66Compare extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;
  /** @type {() => void} */
  #cancelSweep = () => {};
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    const input = this.querySelector('input[type="range"]');
    if (!(input instanceof HTMLInputElement)) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const apply = () => this.style.setProperty('--t66-pos', `${input.value}%`);

    apply();
    input.addEventListener(
      'input',
      () => {
        this.#cancelSweep();
        apply();
      },
      { signal }
    );

    if (reducedMotion() || !('IntersectionObserver' in window)) return;
    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer?.disconnect();
        this.#cancelSweep = tween(0, 1, 1900, (t) => {
          const value = 50 + 30 * Math.sin(t * Math.PI * 2) * (1 - t * 0.15);
          input.value = String(Math.round(value * 10) / 10);
          apply();
        });
      },
      { threshold: 0.6 }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    this.#cancelSweep();
  }
}

class T66Hotspots extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;
  /** @type {HTMLElement[]} */
  #pins = [];
  /** @type {HTMLElement[]} */
  #notes = [];

  connectedCallback() {
    this.#pins = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-pin]')]);
    this.#notes = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-note]')]);
    if (!this.#notes.length) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#pins.forEach((pin, index) => pin.addEventListener('click', () => this.#select(index), { signal }));
    this.#notes.forEach((note, index) => {
      note.querySelector('[data-note-toggle]')?.addEventListener('click', () => this.#select(index), { signal });
    });

    this.dataset.enhanced = '';
    this.#select(0);
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /** @param {number} index */
  #select(index) {
    this.#notes.forEach((note, i) => {
      const active = i === index;
      if (active) note.dataset.active = '';
      else delete note.dataset.active;
      note.querySelector('[data-note-toggle]')?.setAttribute('aria-expanded', String(active));
    });
    this.#pins.forEach((pin, i) => {
      const active = i === index;
      if (active) pin.dataset.active = '';
      else delete pin.dataset.active;
      pin.setAttribute('aria-pressed', String(active));
    });
  }
}

class T66Countup extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;
  /** @type {() => void} */
  #cancel = () => {};

  connectedCallback() {
    const to = Number(this.dataset.to);
    if (Number.isNaN(to) || reducedMotion() || !('IntersectionObserver' in window)) return;

    const decimals = Number(this.dataset.decimals) || 0;
    const format = new Intl.NumberFormat(document.documentElement.lang || undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    this.textContent = format.format(0);

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer?.disconnect();
        this.#cancel = tween(0, to, 1500, (value) => {
          this.textContent = format.format(value);
        });
      },
      { threshold: 0.4 }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#cancel();
  }
}

class T66Rain extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;
  /** @type {HTMLCanvasElement | null} */
  #canvas = null;
  /** @type {CanvasRenderingContext2D | null} */
  #ctx = null;
  /** @type {{ x: number, y: number, len: number, speed: number, alpha: number }[]} */
  #drops = [];
  #frame = 0;
  #last = 0;
  #width = 0;
  #height = 0;
  #wind = 0.2;
  #targetWind = 0.2;
  #enabled = false;
  #inView = false;
  /** @type {Element} */
  #host = this;
  /** @type {IntersectionObserver | undefined} */
  #observer;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    this.#canvas = this.querySelector('canvas');
    this.#ctx = this.#canvas?.getContext('2d') ?? null;
    if (!this.#canvas || !this.#ctx) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#host = this.closest('section') ?? this;
    this.#host.querySelector('[data-rain-toggle]')?.addEventListener('click', () => this.#setEnabled(!this.#enabled), { signal });

    const area = this.closest('[data-t66-tilt-area]') ?? this.parentElement;
    if (area && finePointer()) {
      area.addEventListener(
        'pointermove',
        (event) => {
          const rect = area.getBoundingClientRect();
          if (!rect.width) return;
          this.#targetWind = ((event.clientX - rect.left) / rect.width - 0.5) * 1.1 + 0.1;
        },
        { signal }
      );
    }
    document.addEventListener('visibilitychange', () => this.#sync(), { signal });

    this.#resizeObserver = new ResizeObserver(() => this.#resize());
    this.#resizeObserver.observe(this);
    this.#observer = new IntersectionObserver((entries) => {
      this.#inView = entries.some((entry) => entry.isIntersecting);
      this.#sync();
    });
    this.#observer.observe(this);

    this.#setEnabled(!reducedMotion());
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  /** @param {boolean} enabled */
  #setEnabled(enabled) {
    this.#enabled = enabled;
    this.#host.querySelector('[data-rain-toggle]')?.setAttribute('aria-pressed', String(enabled));
    const label = this.#host.querySelector('[data-rain-label]');
    if (label) label.textContent = (enabled ? this.dataset.textOn : this.dataset.textOff) ?? '';
    this.#sync();
  }

  #sync() {
    const run = this.#enabled && this.#inView && !document.hidden;
    if (run && !this.#frame) {
      this.#last = 0;
      this.#frame = requestAnimationFrame((now) => this.#tick(now));
    } else if (!run) {
      cancelAnimationFrame(this.#frame);
      this.#frame = 0;
      this.#ctx?.clearRect(0, 0, this.#width, this.#height);
    }
  }

  #resize() {
    if (!this.#canvas || !this.#ctx) return;
    const rect = this.getBoundingClientRect();
    const ratio = Math.min(window.devicePixelRatio || 1, 1.5);
    this.#width = rect.width;
    this.#height = rect.height;
    this.#canvas.width = Math.round(rect.width * ratio);
    this.#canvas.height = Math.round(rect.height * ratio);
    this.#ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    const count = Math.min(260, Math.round((rect.width * rect.height) / 9000));
    this.#drops = Array.from({ length: count }, () => this.#drop(true));
  }

  /** @param {boolean} anywhere */
  #drop(anywhere) {
    return {
      x: Math.random() * (this.#width + 200) - 100,
      y: anywhere ? Math.random() * this.#height : -40,
      len: 14 + Math.random() * 26,
      speed: 16 + Math.random() * 18,
      alpha: 0.12 + Math.random() * 0.34,
    };
  }

  /** @param {number} now */
  #tick(now) {
    const ctx = this.#ctx;
    if (!ctx) return;
    const dt = Math.min(48, now - (this.#last || now - 16)) / 16;
    this.#last = now;
    this.#wind += (this.#targetWind - this.#wind) * 0.04;

    ctx.clearRect(0, 0, this.#width, this.#height);
    ctx.lineWidth = 1.1;
    ctx.lineCap = 'round';
    for (let i = 0; i < this.#drops.length; i++) {
      const drop = this.#drops[i];
      drop.y += drop.speed * dt;
      drop.x += drop.speed * this.#wind * dt;
      if (drop.y - drop.len > this.#height || drop.x > this.#width + 120 || drop.x < -160) {
        this.#drops[i] = this.#drop(false);
        continue;
      }
      ctx.strokeStyle = `rgba(232, 219, 202, ${drop.alpha})`;
      ctx.beginPath();
      ctx.moveTo(drop.x, drop.y);
      ctx.lineTo(drop.x - this.#wind * drop.len, drop.y - drop.len);
      ctx.stroke();
    }
    this.#frame = requestAnimationFrame((time) => this.#tick(time));
  }
}

class T66Strips extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;
  /** @type {HTMLElement[]} */
  #strips = [];

  connectedCallback() {
    this.#strips = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-strip]')]);
    if (this.#strips.length < 2) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#strips.forEach((strip, index) => {
      const toggle = strip.querySelector('[data-strip-toggle]');
      toggle?.addEventListener('click', () => this.#select(index), { signal });
      toggle?.addEventListener('focus', () => this.#select(index), { signal });
      if (finePointer()) strip.addEventListener('pointerenter', () => this.#select(index), { signal });
    });

    this.dataset.enhanced = '';
    this.#select(0);
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /** @param {number} index */
  #select(index) {
    this.#strips.forEach((strip, i) => {
      const active = i === index;
      if (active) strip.dataset.active = '';
      else delete strip.dataset.active;
      strip.querySelector('[data-strip-toggle]')?.setAttribute('aria-expanded', String(active));
    });
  }
}

class T66Reveal extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    if (reducedMotion() || !('IntersectionObserver' in window)) return;
    this.dataset.ready = '';
    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer?.disconnect();
        this.dataset.visible = '';
      },
      { threshold: 0.25 }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }
}

class T66Scrolly extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;
  #frame = 0;
  /** @type {HTMLElement[]} */
  #steps = [];
  /** @type {HTMLElement[]} */
  #dots = [];
  #index = -1;

  connectedCallback() {
    this.#steps = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-scrolly-step]')]);
    this.#dots = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-scrolly-go]')]);
    if (this.#steps.length < 2) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const wide = matchMedia('(min-width: 900px)');
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => {
      if (wide.matches && !motion.matches) {
        this.dataset.enhanced = '';
        this.#update();
      } else {
        delete this.dataset.enhanced;
        this.#index = -1;
      }
    };

    wide.addEventListener('change', sync, { signal });
    motion.addEventListener('change', sync, { signal });
    // En escritorio el tema scrollea dentro de .page-wrapper, no en la ventana; los eventos scroll no burbujean,
    // así que se escuchan en captura desde document para cubrir ambos casos.
    document.addEventListener('scroll', () => this.#queue(), { passive: true, capture: true, signal });
    window.addEventListener('resize', () => this.#queue(), { signal });
    this.#dots.forEach((dot, i) => dot.addEventListener('click', () => this.#goto(i), { signal }));
    sync();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  #queue() {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  }

  /** Progreso 0–1 de la sección y paso activo según el scroll. */
  #update() {
    if (!('enhanced' in this.dataset)) return;
    const rect = this.getBoundingClientRect();
    const span = rect.height - window.innerHeight;
    if (span <= 0) return;

    const progress = Math.min(1, Math.max(0, -rect.top / span));
    const count = this.#steps.length;
    const index = Math.min(count - 1, Math.floor(progress * count));
    this.style.setProperty('--t66-p', progress.toFixed(4));
    if (index === this.#index) return;

    this.#index = index;
    this.#steps.forEach((step, i) => {
      if (i === index) step.dataset.on = '';
      else delete step.dataset.on;
      if (i <= index) step.dataset.seen = '';
      else delete step.dataset.seen;
    });
    this.#dots.forEach((dot, i) => {
      if (i === index) dot.setAttribute('aria-current', 'step');
      else dot.removeAttribute('aria-current');
    });
    const counter = this.querySelector('[data-scrolly-count]');
    if (counter) counter.textContent = String(index + 1).padStart(2, '0');
  }

  /** @param {number} index */
  #goto(index) {
    if (!('enhanced' in this.dataset)) return;
    const rect = this.getBoundingClientRect();
    const span = rect.height - window.innerHeight;
    const behavior = reducedMotion() ? 'auto' : 'smooth';
    const offset = span * ((index + 0.5) / this.#steps.length);
    const wrapper = document.querySelector('.page-wrapper');
    if (wrapper instanceof HTMLElement && wrapper.scrollHeight > wrapper.clientHeight && getComputedStyle(wrapper).overflowY !== 'visible') {
      wrapper.scrollTo({ top: wrapper.scrollTop + rect.top - wrapper.getBoundingClientRect().top + offset, behavior });
    } else {
      window.scrollTo({ top: window.scrollY + rect.top + offset, behavior });
    }
  }
}

const elements = {
  't66-tilt': T66Tilt,
  't66-stage': T66Stage,
  't66-compare': T66Compare,
  't66-hotspots': T66Hotspots,
  't66-countup': T66Countup,
  't66-rain': T66Rain,
  't66-strips': T66Strips,
  't66-reveal': T66Reveal,
  't66-scrolly': T66Scrolly,
};
for (const [name, element] of Object.entries(elements)) {
  if (!customElements.get(name)) customElements.define(name, element);
}
